import React, { useCallback, useEffect, useRef, useState } from 'react';
import { NodeViewWrapper, NodeViewProps } from '@tiptap/react';
import { cn } from '@/lib/utils';

const ResizableImage: React.FC<NodeViewProps> = ({
  node,
  updateAttributes,
  selected,
}) => {
  const imgRef = useRef<HTMLImageElement>(null);
  const [isResizing, setIsResizing] = useState(false);
  const [resizeDirection, setResizeDirection] = useState<string | null>(null);
  const startPos = useRef({ x: 0, y: 0, width: 0, height: 0 });

  const { src, alt, title, width, height, float, display } = node.attrs;

  // Build inline styles
  const getImageStyles = (): React.CSSProperties => {
    const styles: React.CSSProperties = {};

    if (float && float !== 'none') {
      styles.float = float;
      styles.margin = float === 'left' ? '0 16px 8px 0' : '0 0 8px 16px';
    }

    if (display === 'block') {
      styles.display = 'block';
      styles.marginLeft = 'auto';
      styles.marginRight = 'auto';
    }

    if (width) {
      styles.width = `${width}px`;
    }

    if (height) {
      styles.height = `${height}px`;
    }

    return styles;
  };

  const handleMouseDown = useCallback(
    (e: React.MouseEvent, direction: string) => {
      e.preventDefault();
      e.stopPropagation();

      if (!imgRef.current) return;

      const rect = imgRef.current.getBoundingClientRect();
      startPos.current = {
        x: e.clientX,
        y: e.clientY,
        width: rect.width,
        height: rect.height,
      };
      setIsResizing(true);
      setResizeDirection(direction);
    },
    []
  );

  const handleMouseMove = useCallback(
    (e: MouseEvent) => {
      if (!isResizing || !resizeDirection) return;

      const deltaX = e.clientX - startPos.current.x;
      const deltaY = e.clientY - startPos.current.y;

      let newWidth = startPos.current.width;
      let newHeight = startPos.current.height;

      // Handle different resize directions
      if (resizeDirection.includes('e')) {
        newWidth = Math.max(50, startPos.current.width + deltaX);
      }
      if (resizeDirection.includes('w')) {
        newWidth = Math.max(50, startPos.current.width - deltaX);
      }
      if (resizeDirection.includes('s')) {
        newHeight = Math.max(50, startPos.current.height + deltaY);
      }
      if (resizeDirection.includes('n')) {
        newHeight = Math.max(50, startPos.current.height - deltaY);
      }

      // For corner handles, maintain aspect ratio
      if (resizeDirection.length === 2) {
        const aspectRatio = startPos.current.width / startPos.current.height;
        if (Math.abs(deltaX) > Math.abs(deltaY)) {
          newHeight = newWidth / aspectRatio;
        } else {
          newWidth = newHeight * aspectRatio;
        }
      }

      updateAttributes({
        width: Math.round(newWidth),
        height: Math.round(newHeight),
      });
    },
    [isResizing, resizeDirection, updateAttributes]
  );

  const handleMouseUp = useCallback(() => {
    setIsResizing(false);
    setResizeDirection(null);
  }, []);

  useEffect(() => {
    if (isResizing) {
      document.addEventListener('mousemove', handleMouseMove);
      document.addEventListener('mouseup', handleMouseUp);
      return () => {
        document.removeEventListener('mousemove', handleMouseMove);
        document.removeEventListener('mouseup', handleMouseUp);
      };
    }
  }, [isResizing, handleMouseMove, handleMouseUp]);

  const handleStyles: React.CSSProperties = {
    position: 'absolute',
    width: '10px',
    height: '10px',
    background: 'hsl(var(--primary))',
    borderRadius: '50%',
    border: '2px solid white',
    boxShadow: '0 2px 4px rgba(0,0,0,0.2)',
  };

  return (
    <NodeViewWrapper
      as="span"
      className={cn('relative inline-block', isResizing && 'select-none')}
      style={{ display: display === 'block' ? 'block' : 'inline-block' }}
    >
      <img
        ref={imgRef}
        src={src}
        alt={alt || ''}
        title={title}
        style={getImageStyles()}
        className={cn(
          'rounded cursor-pointer transition-shadow',
          selected && 'outline outline-2 outline-primary outline-offset-2'
        )}
        draggable={false}
      />
      
      {/* Resize handles - only show when selected */}
      {selected && (
        <>
          {/* Corner handles */}
          <div
            style={{ ...handleStyles, top: -5, left: -5, cursor: 'nwse-resize' }}
            onMouseDown={(e) => handleMouseDown(e, 'nw')}
          />
          <div
            style={{ ...handleStyles, top: -5, right: -5, cursor: 'nesw-resize' }}
            onMouseDown={(e) => handleMouseDown(e, 'ne')}
          />
          <div
            style={{ ...handleStyles, bottom: -5, left: -5, cursor: 'nesw-resize' }}
            onMouseDown={(e) => handleMouseDown(e, 'sw')}
          />
          <div
            style={{ ...handleStyles, bottom: -5, right: -5, cursor: 'nwse-resize' }}
            onMouseDown={(e) => handleMouseDown(e, 'se')}
          />
          
          {/* Edge handles */}
          <div
            style={{ ...handleStyles, top: '50%', left: -5, transform: 'translateY(-50%)', cursor: 'ew-resize' }}
            onMouseDown={(e) => handleMouseDown(e, 'w')}
          />
          <div
            style={{ ...handleStyles, top: '50%', right: -5, transform: 'translateY(-50%)', cursor: 'ew-resize' }}
            onMouseDown={(e) => handleMouseDown(e, 'e')}
          />
          <div
            style={{ ...handleStyles, left: '50%', top: -5, transform: 'translateX(-50%)', cursor: 'ns-resize' }}
            onMouseDown={(e) => handleMouseDown(e, 'n')}
          />
          <div
            style={{ ...handleStyles, left: '50%', bottom: -5, transform: 'translateX(-50%)', cursor: 'ns-resize' }}
            onMouseDown={(e) => handleMouseDown(e, 's')}
          />
        </>
      )}
    </NodeViewWrapper>
  );
};

export default ResizableImage;
